import { animate as motionAnimate, timeline, inView } from "//earthnutri.com/cdn/shop/t/18/assets/vendor.min.js?v=110209841862038618801702591039";
import { EffectCarousel, imageLoaded, getHeadingKeyframe } from "//earthnutri.com/cdn/shop/t/18/assets/theme.js?v=141012634197974557371792062640";

var Slideshow = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("carousel:select", this._onSlideSelected);
  }
  async _onSlideSelected(event) {
    const slideStyles = getComputedStyle(event.detail.slide);
    this.style.setProperty("--slideshow-controls-background", slideStyles.getPropertyValue("--slideshow-slide-controls-background"));
    this.style.setProperty("--slideshow-controls-color", slideStyles.getPropertyValue("--slideshow-slide-controls-color"));
    if (!this.classList.contains("slideshow--boxed")) {
      return;
    }
    const backgroundElement = this.querySelector(".slideshow__slide-background");
    const background = slideStyles.getPropertyValue("--slideshow-slide-background");
    backgroundElement.style.background = background;
    await motionAnimate(backgroundElement, { opacity: [0, 1] }, { duration: 0.2 }).finished;
    this.style.setProperty("--slideshow-background", background);
    motionAnimate(backgroundElement, { opacity: 0 }, { duration: 0 });
  }
};

var SlideshowCarousel = class extends EffectCarousel {
  constructor() {
    super();
    if (this.querySelector(".slideshow__cursor")) {
      this.addEventListener("tap", this._onSlideshowTap);
    }
  }
  connectedCallback() {
    super.connectedCallback();

    // Header-group sections also carry .shopify-section, so the hero is the first
    // section inside #main, not the first one in the document.
    const parentSection = this.closest(".shopify-section");
    const isHero = parentSection && parentSection === document.querySelector("#main .shopify-section");
    if (isHero) {
      // Static hero: the server-rendered content is already fully visible. Any
      // reveal animation would set it to opacity 0 before first paint and push
      // back LCP, so do nothing at all.
      this.style.opacity = "1";
    } else {
      inView(this, this._reveal.bind(this));
    }

    if (this._player && this.hasAttribute("autoplay")) {
      this._player.addEventListener("player:start", () => {
        const cursorRing = this.querySelector(".slideshow__cursor-ring circle");
        if (cursorRing) {
          motionAnimate(cursorRing, {
            strokeDasharray: [`0px, ${cursorRing.getTotalLength()}px`, `${cursorRing.getTotalLength()}px, ${cursorRing.getTotalLength()}px`]
          }, { duration: parseInt(this.getAttribute("autoplay")), easing: "linear" });
        }
        Array.from(this.querySelectorAll(".numbered-dots__item")).forEach((item) => {
          const circle = item.querySelector("circle:last-child");
          if (item.getAttribute("aria-current") === "true") {
            motionAnimate(circle, {
              strokeDasharray: [`0px, ${circle.getTotalLength()}px`, `${circle.getTotalLength()}px, ${circle.getTotalLength()}px`]
            }, { duration: parseInt(this.getAttribute("autoplay")), easing: "linear" });
          } else {
            motionAnimate(circle, { strokeDasharray: `${circle.getTotalLength()}px, ${circle.getTotalLength()}px` }, { duration: 0 });
          }
        });
      });
    }
  }
  get transitionType() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "fade" : this.getAttribute("transition");
  }
  _reveal() {
    const selectedSlide = this.selectedSlide;

    this.style.opacity = "1";

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || this._reloaded) {
      return;
    }

    // Only runs for below-the-fold slideshows (the hero skips _reveal entirely).
    // Still never animate the slide image — opacity/transform mutations on it can
    // re-record paint timestamps; animate only text overlays and controls.
    timeline([
      [selectedSlide, { zIndex: 1 }, { duration: 0 }],
      [selectedSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3 }],
      [...getHeadingKeyframe(selectedSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3 })],
      [selectedSlide.querySelector(".button"), { opacity: [0, 1] }, { duration: 0.3 }],
      [this.querySelector(".slideshow__controls"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }]
    ]);
  }
  _onSlideshowTap(event) {
    if (event.target.matches("button, a[href], button :scope, a[href] :scope") || !window.matchMedia("screen and (pointer: fine)").matches) {
      return;
    }
    event.detail.originalEvent.clientX > window.innerWidth / 2 ? this.next() : this.previous();
  }
  async _transitionTo(fromSlide, toSlide, { direction, animate = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    let timelineControls = null;
    switch (this.transitionType) {
      case "fade":
        timelineControls = this._fade(fromSlide, toSlide);
        break;
      case "fade_with_text":
        timelineControls = this._fadeWithText(fromSlide, toSlide);
        break;
    }
    if (!animate) {
      timelineControls.finish();
    }
    return timelineControls.finished;
  }
  _fade(fromSlide, toSlide) {
    return timeline([
      [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } }],
      [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0.3, at: "<", easing: "ease-out", zIndex: { easing: "step-end" } }]
    ]);
  }
  async _fadeWithText(fromSlide, toSlide) {
    motionAnimate(fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } });
    await imageLoaded(toSlide.querySelectorAll("img"));
    motionAnimate(toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0, zIndex: { easing: "step-end" } });
    return timeline([
      [toSlide.querySelectorAll("img"), { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3, easing: "ease-out" }],
      "content",
      [toSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
      [...getHeadingKeyframe(toSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3, at: "content" })]
    ]);
  }
};

if (!window.customElements.get("en-x-slideshow")) {
  window.customElements.define("en-x-slideshow", Slideshow);
}
if (!window.customElements.get("en-slideshow-carousel")) {
  window.customElements.define("en-slideshow-carousel", SlideshowCarousel);
}
